import { existsSync, readFileSync, writeFileSync, mkdirSync } from "node:fs";
import { join } from "node:path";
import { homedir } from "node:os";
import { spawnSync } from "node:child_process";

interface McpInstallResult {
  status: "registered" | "already_registered" | "claude_not_found" | "failed";
  message?: string;
}

/** Check if the cai MCP server is already registered in a settings file */
function isMcpRegistered(settingsPath: string): boolean {
  if (!existsSync(settingsPath)) return false;
  try {
    const raw = readFileSync(settingsPath, "utf8");
    const json = JSON.parse(raw) as Record<string, unknown>;
    const servers = json.mcpServers as Record<string, unknown> | undefined;
    return Boolean(servers?.cai);
  } catch {
    return false;
  }
}

/** Write cai MCP entry directly into a settings file (fallback when claude CLI unavailable) */
function writeDirectly(settingsPath: string): void {
  let json: Record<string, unknown> = {};
  if (existsSync(settingsPath)) {
    try {
      json = JSON.parse(readFileSync(settingsPath, "utf8")) as Record<string, unknown>;
    } catch {
      // file exists but invalid JSON — start fresh
    }
  }

  const servers = (json.mcpServers ?? {}) as Record<string, unknown>;
  servers.cai = { command: "cai", args: ["mcp", "start"] };
  json.mcpServers = servers;

  mkdirSync(join(settingsPath, ".."), { recursive: true });
  writeFileSync(settingsPath, JSON.stringify(json, null, 2) + "\n", "utf8");
}

/**
 * Ensure the cai MCP server is registered with Claude Code.
 * Called automatically after `cai setup` and `cai update`.
 */
export function ensureMcpRegistered(projectRoot: string): McpInstallResult {
  // Check project-level settings first, then global
  const projectSettings = join(projectRoot, ".claude", "settings.json");
  const globalSettings = join(homedir(), ".claude", "settings.json");

  if (isMcpRegistered(projectSettings) || isMcpRegistered(globalSettings)) {
    return { status: "already_registered" };
  }

  // Try the claude CLI first (cleanest, respects its own config)
  const result = spawnSync("claude", ["mcp", "add", "cai", "--", "cai", "mcp", "start"], {
    cwd: projectRoot,
    stdio: "pipe",
  });

  if (result.error) {
    const code = (result.error as NodeJS.ErrnoException).code;
    if (code === "ENOENT") {
      // claude CLI not installed — write directly to project settings file
      try {
        writeDirectly(projectSettings);
        return {
          status: "registered",
          message: "written to .claude/settings.json (claude CLI not found)",
        };
      } catch (err) {
        return { status: "claude_not_found" };
      }
    }
    return { status: "failed", message: result.error.message };
  }

  // claude CLI succeeded or already registered via CLI
  const stderr = result.stderr?.toString() ?? "";
  if (result.status !== 0 && stderr.toLowerCase().includes("already")) {
    return { status: "already_registered" };
  }

  if (result.status !== 0) {
    // Fallback: write directly
    try {
      writeDirectly(projectSettings);
      return { status: "registered" };
    } catch {
      return { status: "failed", message: stderr.trim() || "unknown error" };
    }
  }

  return { status: "registered" };
}

/**
 * Install CAI-recommended default hooks into .claude/settings.json.
 * Returns the number of new hooks installed.
 *
 * Default hooks installed:
 * - PreCompact: reminds Claude to re-read context after compaction
 * - PreToolUse: warns on force push attempts (Bash matcher)
 * - Stop:       runs `cai verify --hook` so the agent re-engages on failure
 *
 * Privacy-sensitive hooks (cai learn record, cai session --auto) are NOT
 * installed here — they require explicit opt-in via their own commands.
 *
 * Hook key names and structure follow Claude Code's settings.json schema:
 * https://docs.claude.com/en/docs/claude-code/hooks
 */
export function ensureCaiHooks(projectRoot: string): number {
  const settingsPath = join(projectRoot, ".claude", "settings.json");

  let json: Record<string, unknown> = {};
  if (existsSync(settingsPath)) {
    try {
      json = JSON.parse(readFileSync(settingsPath, "utf8")) as Record<string, unknown>;
    } catch {
      // invalid JSON — start fresh
    }
  }

  const hooks = (json.hooks ?? {}) as Record<string, unknown>;
  let installed = 0;

  // Migrate legacy lowercase keys from older CAI versions
  if (hooks.postCompact) {
    delete hooks.postCompact;
  }
  if (hooks.preToolUse) {
    delete hooks.preToolUse;
  }

  // PreCompact: re-read context after compaction
  if (!hooks.PreCompact) {
    hooks.PreCompact = [
      {
        matcher: "",
        hooks: [
          {
            type: "command",
            command: "echo 'Context was compacted. Re-read CLAUDE.md and .cai/ROUTER.md before continuing. Verify your current task and plan are still loaded.'",
          },
        ],
      },
    ];
    installed++;
  }

  // PreToolUse: warn before bash commands (force-push detection happens in command)
  if (!hooks.PreToolUse) {
    hooks.PreToolUse = [
      {
        matcher: "Bash",
        hooks: [
          {
            type: "command",
            command:
              "jq -r '.tool_input.command // empty' | grep -qE 'git +push.*--force' && { echo 'BLOCKED: Force push detected.' >&2; exit 2; } || exit 0",
          },
        ],
      },
    ];
    installed++;
  }

  // Stop: run cai verify after agent completes — back-pressure for AI agents
  // We check if the verify command is already wired into any Stop block before adding ours.
  const existingStopHooks = (hooks.Stop ?? []) as Array<{ matcher?: string; hooks?: Array<{ type: string; command: string }> }>;
  const verifyAlreadyPresent = existingStopHooks.some((block) =>
    (block.hooks ?? []).some((h) => (h.command || "").includes("cai verify --hook"))
  );

  if (!verifyAlreadyPresent) {
    const verifyBlock = {
      matcher: "",
      hooks: [{ type: "command", command: "cai verify --hook" }],
    };
    hooks.Stop = existingStopHooks.length === 0 ? [verifyBlock] : [...existingStopHooks, verifyBlock];
    installed++;
  }

  if (installed > 0 || Object.keys(hooks).length > 0) {
    json.hooks = hooks;
    mkdirSync(join(settingsPath, ".."), { recursive: true });
    writeFileSync(settingsPath, JSON.stringify(json, null, 2) + "\n", "utf8");
  }

  return installed;
}

/**
 * Install the UserPromptSubmit hook that pipes prompts into `cai learn record`.
 *
 * Separated from `ensureCaiHooks` because it's privacy-sensitive — only added
 * when the user explicitly opts into recording via `cai learn enable` (or runs
 * `cai learn install-hook` directly).
 *
 * Returns:
 *   "installed"        — fresh install
 *   "already-present"  — exact same hook already there, no change
 *   "merged"           — there were existing UserPromptSubmit hooks; ours was appended
 */
export type LearnHookResult = "installed" | "already-present" | "merged";

export function ensureLearnHook(projectRoot: string): LearnHookResult {
  return installHook(projectRoot, "UserPromptSubmit", "cai learn record");
}

/**
 * Install the UserPromptSubmit hook that prepends a tiny session context
 * (uncommitted files, recent commits, hot files, drift) to every prompt.
 *
 * Like ensureLearnHook this is opt-in — the user runs `cai session install-hook`.
 */
export function ensureSessionAutoHook(projectRoot: string): LearnHookResult {
  return installHook(projectRoot, "UserPromptSubmit", "cai session --auto");
}

export function removeSessionAutoHook(projectRoot: string): boolean {
  return removeHook(projectRoot, "UserPromptSubmit", "cai session --auto");
}

/**
 * Install the Stop hook that runs `cai verify` after every agent stop.
 * This is the back-pressure mechanism: if verification fails, exit code 2
 * tells Claude Code to re-engage the agent so it can fix the issue.
 */
export function ensureVerifyHook(projectRoot: string): LearnHookResult {
  return installHook(projectRoot, "Stop", "cai verify --hook");
}

export function removeVerifyHook(projectRoot: string): boolean {
  return removeHook(projectRoot, "Stop", "cai verify --hook");
}

/**
 * Install the PreToolUse hook that injects CAI drift context before Edit/Write calls.
 * Fires only when Claude edits a source file that is referenced in the scaffold.
 * guidanceOnce throttling is handled inside `cai hook predrift` via /tmp marker.
 */
export function ensurePredriftHook(projectRoot: string): LearnHookResult {
  return installHook(projectRoot, "PreToolUse", "cai hook predrift", "Edit|Write");
}

export function removePredriftHook(projectRoot: string): boolean {
  return removeHook(projectRoot, "PreToolUse", "cai hook predrift");
}

type HookBlock = { matcher?: string; hooks?: Array<{ type: string; command: string }> };
type HookEvent = "Stop" | "UserPromptSubmit" | "PreCompact" | "PreToolUse";

/**
 * Generic installer for any Claude Code hook event.
 * Used by learn / session-auto / verify wrappers — they just bind a different
 * `event` and `command` and reuse this body.
 */
function installHook(projectRoot: string, event: HookEvent, command: string, matcher = ""): LearnHookResult {
  const settingsPath = join(projectRoot, ".claude", "settings.json");

  let json: Record<string, unknown> = {};
  if (existsSync(settingsPath)) {
    try {
      json = JSON.parse(readFileSync(settingsPath, "utf8")) as Record<string, unknown>;
    } catch {
      // invalid JSON — start fresh
    }
  }

  const hooks = (json.hooks ?? {}) as Record<string, unknown>;
  const existing = (hooks[event] ?? []) as HookBlock[];

  for (const block of existing) {
    if (!block.hooks) continue;
    for (const h of block.hooks) {
      if (h.command && h.command.includes(command)) {
        return "already-present";
      }
    }
  }

  const newBlock: HookBlock = {
    matcher,
    hooks: [{ type: "command", command }],
  };

  const result: LearnHookResult = existing.length === 0 ? "installed" : "merged";
  hooks[event] = existing.length === 0 ? [newBlock] : [...existing, newBlock];

  json.hooks = hooks;
  mkdirSync(join(settingsPath, ".."), { recursive: true });
  writeFileSync(settingsPath, JSON.stringify(json, null, 2) + "\n", "utf8");
  return result;
}

/**
 * Generic remover for any Claude Code hook event.
 * Removes only blocks containing the given command fragment — other hooks
 * (from other tools or other CAI commands) are preserved.
 */
function removeHook(projectRoot: string, event: HookEvent, commandFragment: string): boolean {
  const settingsPath = join(projectRoot, ".claude", "settings.json");
  if (!existsSync(settingsPath)) return false;

  let json: Record<string, unknown>;
  try {
    json = JSON.parse(readFileSync(settingsPath, "utf8")) as Record<string, unknown>;
  } catch {
    return false;
  }

  const hooks = (json.hooks ?? {}) as Record<string, unknown>;
  const existing = (hooks[event] ?? []) as HookBlock[];
  if (existing.length === 0) return false;

  let changed = false;
  const filtered = existing
    .map((block) => {
      if (!block.hooks) return block;
      const before = block.hooks.length;
      const remaining = block.hooks.filter((h) => !(h.command || "").includes(commandFragment));
      if (remaining.length !== before) changed = true;
      return { ...block, hooks: remaining };
    })
    .filter((block) => (block.hooks ?? []).length > 0);

  if (!changed) return false;

  if (filtered.length === 0) {
    delete hooks[event];
  } else {
    hooks[event] = filtered;
  }
  json.hooks = hooks;
  writeFileSync(settingsPath, JSON.stringify(json, null, 2) + "\n", "utf8");
  return true;
}

/**
 * Remove the cai learn UserPromptSubmit hook. Other UserPromptSubmit hooks
 * (from other tools or from cai session --auto) are preserved.
 */
export function removeLearnHook(projectRoot: string): boolean {
  return removeHook(projectRoot, "UserPromptSubmit", "cai learn record");
}
